import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { HEADER_H, PUBLIC_ROUTES, useTestTransition } from './TestTransitionContext';

const NAV_H = 100;
const MENU_SVG_HEIGHT = '122%';
const LOGO_AXIS_X = 40;
const LOGO_AXIS_Y = 64;
const LOGO_WIDTH = 860;
const LOGO_SLANT = 260;
const LOGO_HEIGHT = 500;
const LABEL_Y_OFFSET = -10;

const startTL = { x: 300, y: 460 };
const shapes = Array.from({ length: 12 }, (_, i) => {
    const TL = { x: startTL.x + LOGO_AXIS_X * i, y: startTL.y + LOGO_AXIS_Y * i };
    const TR = { x: TL.x + LOGO_WIDTH, y: TL.y };
    const BL = { x: TL.x - LOGO_SLANT, y: TL.y + LOGO_HEIGHT };
    const BR = { x: TR.x - LOGO_SLANT, y: TR.y + LOGO_HEIGHT };
    return { TL, TR, BR, BL };
});

const MENU_ITEMS = [
    { label: 'Chi Siamo', route: PUBLIC_ROUTES.chisiamo },
    { label: 'Progetti', route: PUBLIC_ROUTES.progetti },
    { label: 'Finanza di Progetto', route: PUBLIC_ROUTES.finanza },
    { label: 'Lavora con Noi', route: PUBLIC_ROUTES.lavora },
    { label: 'Contatti', route: PUBLIC_ROUTES.contatti },
];

function shapeOpacity(i) {
    const squareOpacity = [1, 0.9, 0.78, 0.66, 0.54, 0.42, 0.31, 0.22, 0.14, 0.11, 0.09, 0.08];
    return squareOpacity[i] ?? 0.08;
}

function logoTopRightClient(svg, index) {
    const sh = shapes[index];
    if (!svg || !sh) return null;
    const pt = svg.createSVGPoint();
    pt.x = sh.TR.x;
    pt.y = sh.TR.y;
    return pt.matrixTransform(svg.getScreenCTM());
}

function headerLayoutFor(route, overlay, visibleExtra = 0) {
    const header = document.querySelector('.esi-transition-header');
    const fallbackH = HEADER_H[route] || 363;
    if (!header || !overlay) {
        const topInset = route === PUBLIC_ROUTES.progetti ? 0 : 168;
        const visibleHeight = fallbackH + visibleExtra;
        return {
            height: fallbackH,
            topInset,
            clip: `inset(${topInset}px 0 calc(100% - ${topInset + visibleHeight}px) 0)`,
            rect: null,
        };
    }

    const headerRect = header.getBoundingClientRect();
    const overlayRect = overlay.getBoundingClientRect();
    const topInset = Math.max(0, Math.round(headerRect.top - overlayRect.top));
    const height = Math.round(headerRect.height || fallbackH);
    const visibleHeight = height + visibleExtra;
    return {
        height,
        topInset,
        clip: `inset(${topInset}px 0 calc(100% - ${topInset + visibleHeight}px) 0)`,
        rect: headerRect,
    };
}

function resetSvg(svg) {
    svg.setAttribute('viewBox', '-110 -50 1600 1000');
    svg.setAttribute('preserveAspectRatio', 'xMidYMax slice');
    svg.style.width = 'auto';
    svg.style.height = MENU_SVG_HEIGHT;
    svg.style.maxWidth = 'none';
    svg.style.overflow = 'visible';
    svg.style.transition = 'none';
    svg.style.transform = 'translate(0,0)';
}

function clearHeaderClone(host) {
    if (!host) return;
    host.replaceChildren();
    host.style.display = 'none';
    host.style.opacity = '0';
}

function mountHeaderClone(host, overlay) {
    const header = document.querySelector('.esi-transition-header');
    if (!host || !overlay || !header) return false;

    const headerRect = header.getBoundingClientRect();
    const overlayRect = overlay.getBoundingClientRect();
    if (!headerRect.width || !headerRect.height) return false;

    const clone = header.cloneNode(true);
    clone.classList.add('esi-transition-header-clone');
    clone.setAttribute('aria-hidden', 'true');
    clone.style.width = '100%';
    clone.style.height = '100%';
    clone.style.margin = '0';
    clone.style.visibility = 'visible';

    host.replaceChildren(clone);
    host.style.display = 'block';
    host.style.left = `${Math.round(headerRect.left - overlayRect.left)}px`;
    host.style.top = `${Math.round(headerRect.top - overlayRect.top)}px`;
    host.style.width = `${Math.round(headerRect.width)}px`;
    host.style.height = `${Math.round(headerRect.height)}px`;
    host.style.opacity = '0';
    host.style.transition = 'opacity 0.24s ease-out';
    return true;
}

export default function MenuSvgOverlay() {
    const location = useLocation();
    const { phase, targetRoute, navTo, openMenu } = useTestTransition();

    const overlayRef = useRef(null);
    const bgRef = useRef(null);
    const svgRef = useRef(null);
    const headerCloneRef = useRef(null);
    const shapeRefs = useRef([]);
    const labelRefs = useRef([]);

    useEffect(() => {
        if (location.pathname === PUBLIC_ROUTES.home && phase === 'idle') {
            openMenu();
        }
    }, [location.pathname, phase, openMenu]);

    useEffect(() => {
        document.body.classList.toggle(
            'esi-menu-morphing',
            phase === 'morphToHeader' || phase === 'openFromHeader'
        );
        document.body.classList.toggle('esi-menu-to-header', phase === 'morphToHeader');
        if (phase !== 'morphToHeader') {
            document.body.classList.remove('esi-menu-reveal-target');
        }

        return () => {
            document.body.classList.remove('esi-menu-morphing');
            document.body.classList.remove('esi-menu-reveal-target');
            document.body.classList.remove('esi-menu-to-header');
        };
    }, [phase]);

    useEffect(() => {
        const ov = overlayRef.current;
        const bg = bgRef.current;
        const svg = svgRef.current;
        const headerClone = headerCloneRef.current;
        if (!ov || !bg || !svg) return;

        const shapeEls = shapeRefs.current.filter(Boolean);
        const labelEls = labelRefs.current.filter(Boolean);
        const setShapes = (transition, opacity, transform) => {
            shapeEls.forEach(el => {
                el.style.transition = transition;
                el.style.opacity = opacity;
                el.style.transform = transform;
            });
        };
        const setLabels = (transition, opacity, transform) => {
            labelEls.forEach(el => {
                el.style.transition = transition;
                el.style.opacity = opacity;
                el.style.transform = transform;
            });
        };

        if (phase === 'menu' || phase === 'menuReady') {
            clearHeaderClone(headerClone);
            resetSvg(svg);
            ov.style.transition = 'none';
            ov.style.visibility = 'visible';
            ov.style.opacity = '1';
            bg.style.transition = 'none';
            bg.style.clipPath = 'inset(0 0 0 0)';
            ov.style.pointerEvents = 'auto';

            if (phase === 'menuReady') {
                setShapes('none', '1', 'translateY(0)');
                setLabels('none', '1', 'translateY(0)');
            } else {
                setShapes('none', '1', 'translateY(0)');
                setLabels('none', '1', 'translateY(0)');
            }
            return;
        }

        if (phase === 'openFromHeader') {
            clearHeaderClone(headerClone);
            resetSvg(svg);
            const layout = headerLayoutFor(location.pathname, ov, 0);
            mountHeaderClone(headerClone, ov);
            if (headerClone?.childElementCount) {
                headerClone.style.transition = 'none';
                headerClone.style.opacity = '1';
            }

            setShapes('none', '1', 'none');
            setLabels('none', '0', 'none');

            ov.style.transition = 'none';
            ov.style.visibility = 'visible';
            ov.style.opacity = '1';
            bg.style.transition = 'none';
            bg.style.clipPath = layout.clip;
            ov.style.pointerEvents = 'auto';

            requestAnimationFrame(() => requestAnimationFrame(() => {
                const sourceTR7 = logoTopRightClient(svg, 6);
                const sourceTR9 = logoTopRightClient(svg, 8);
                const visibleSpanY = sourceTR7 && sourceTR9 ? sourceTR9.y - sourceTR7.y : 0;
                const headerRect = layout.rect;
                const fallbackY = -Math.max(LOGO_AXIS_Y, ov.offsetHeight - layout.height);
                const targetTR7 = headerRect ? {
                    x: headerRect.left + headerRect.width / 2,
                    y: headerRect.top + (headerRect.height - visibleSpanY) / 2,
                } : null;
                const logoMoveX = sourceTR7 && targetTR7 ? targetTR7.x - sourceTR7.x : fallbackY * (LOGO_AXIS_X / LOGO_AXIS_Y);
                const logoMoveY = sourceTR7 && targetTR7 ? targetTR7.y - sourceTR7.y : fallbackY;

                svg.style.transform = `translate(${logoMoveX}px, ${logoMoveY}px)`;
                bg.style.clipPath = layout.clip;
                requestAnimationFrame(() => {
                    svg.style.transition = 'transform 1.2s cubic-bezier(0.4,0,0.2,1)';
                    svg.style.transform = 'translate(0,0)';
                    bg.style.transition = 'clip-path 1.2s cubic-bezier(0.4,0,0.2,1)';
                    bg.style.clipPath = 'inset(0 0 0 0)';
                    if (headerClone?.childElementCount) {
                        headerClone.style.transition = 'opacity 0.24s ease-out';
                        headerClone.style.opacity = '0';
                    }
                    setTimeout(() => {
                        setLabels('opacity 0.22s ease-out', '1', 'none');
                    }, 820);
                });
            }));
            return;
        }

        if (phase === 'morphToHeader') {
            clearHeaderClone(headerClone);
            resetSvg(svg);
            const route = targetRoute || location.pathname;

            ov.style.transition = 'none';
            ov.style.visibility = 'visible';
            ov.style.opacity = '1';
            bg.style.transition = 'none';
            bg.style.clipPath = 'inset(0 0 0 0)';
            ov.style.pointerEvents = 'auto';
            document.body.classList.remove('esi-menu-reveal-target');

            setLabels('opacity 0.18s ease-out', '0', 'none');
            setShapes('none', '1', 'none');

            setTimeout(() => {
                const layout = headerLayoutFor(route, ov, 0);
                mountHeaderClone(headerClone, ov);
                const sourceTR7 = logoTopRightClient(svg, 6);
                const sourceTR9 = logoTopRightClient(svg, 8);
                const visibleSpanY = sourceTR7 && sourceTR9 ? sourceTR9.y - sourceTR7.y : 0;
                const headerRect = layout.rect;
                const fallbackY = -Math.max(LOGO_AXIS_Y, ov.offsetHeight - layout.height);
                const targetTR7 = headerRect ? {
                    x: headerRect.left + headerRect.width / 2,
                    y: headerRect.top + (headerRect.height - visibleSpanY) / 2,
                } : null;
                const logoMoveX = sourceTR7 && targetTR7 ? targetTR7.x - sourceTR7.x : fallbackY * (LOGO_AXIS_X / LOGO_AXIS_Y);
                const logoMoveY = sourceTR7 && targetTR7 ? targetTR7.y - sourceTR7.y : fallbackY;

                svg.style.transition = 'transform 1.2s cubic-bezier(0.4,0,0.2,1)';
                svg.style.transform = `translate(${logoMoveX}px, ${logoMoveY}px)`;

                bg.style.transition = 'clip-path 1.2s cubic-bezier(0.4,0,0.2,1)';
                bg.style.clipPath = layout.clip;
            }, 120);

            setTimeout(() => {
                document.body.classList.add('esi-menu-reveal-target');
                if (headerClone?.childElementCount) {
                    headerClone.style.opacity = '1';
                }
            }, 1260);

            setTimeout(() => {
                setShapes('opacity 0.2s ease-out', '0', 'none');
            }, 1380);
            return;
        }

        if (phase === 'idle') {
            clearHeaderClone(headerClone);
            ov.style.transition = 'none';
            ov.style.visibility = 'hidden';
            ov.style.opacity = '0';
            bg.style.transition = 'none';
            bg.style.clipPath = 'inset(0 0 100% 0)';
            ov.style.pointerEvents = 'none';
            resetSvg(svg);
        }
    }, [phase, targetRoute, location.pathname, openMenu]);

    if (phase === 'idle' && location.pathname !== PUBLIC_ROUTES.home) return null;

    return (
        <>
            <style>
                {`
                    body.esi-menu-morphing .esi-transition-header:not(.esi-transition-header-clone) > * {
                        opacity: 0;
                        transition: opacity 0.32s ease-out;
                    }

                    body.esi-menu-morphing.esi-menu-reveal-target .esi-transition-header:not(.esi-transition-header-clone) > * {
                        opacity: 1;
                    }

                    body.esi-menu-to-header .esi-transition-header:not(.esi-transition-header-clone) {
                        visibility: hidden;
                    }

                    .esi-transition-clone-host .esi-transition-header-clone,
                    .esi-transition-clone-host .esi-transition-header-clone > * {
                        opacity: 1 !important;
                        visibility: visible !important;
                    }

                    .projects-outer-container,
                    .lavora-buttons {
                        transition: opacity 0.32s ease-out;
                    }

                    body.esi-menu-morphing:not(.esi-menu-reveal-target) .projects-outer-container,
                    body.esi-menu-morphing:not(.esi-menu-reveal-target) .lavora-buttons {
                        opacity: 0 !important;
                    }
                `}
            </style>
            <div
            ref={overlayRef}
            style={{
                position: 'fixed',
                top: NAV_H,
                left: 0,
                right: 0,
                bottom: 0,
                opacity: 0,
                visibility: 'hidden',
                pointerEvents: 'none',
                overflow: 'visible',
                background: 'transparent',
                zIndex: 900,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-end',
            }}
        >
                <div
                ref={bgRef}
                style={{
                    position: 'absolute',
                    inset: 0,
                    clipPath: 'inset(0 0 100% 0)',
                    background: 'linear-gradient(90deg, #e3764c 0%, #99191b 100%)',
                    pointerEvents: 'none',
                    zIndex: 0,
                }}
            />
            <div
                ref={headerCloneRef}
                className="esi-transition-clone-host"
                style={{
                    position: 'absolute',
                    display: 'none',
                    opacity: 0,
                    pointerEvents: 'none',
                    overflow: 'hidden',
                    zIndex: 2,
                    willChange: 'opacity',
                }}
            />
            <svg
                ref={svgRef}
                style={{ width: 'auto', height: MENU_SVG_HEIGHT, maxWidth: 'none', overflow: 'visible', willChange: 'transform', position: 'relative', zIndex: 1 }}
                viewBox="-110 -50 1600 1000"
                preserveAspectRatio="xMidYMax slice"
                xmlns="http://www.w3.org/2000/svg"
            >
                {shapes.map((sh, i) => {
                    const d = `M ${sh.TL.x} ${sh.TL.y} L ${sh.TR.x} ${sh.TR.y} L ${sh.BR.x} ${sh.BR.y} L ${sh.BL.x} ${sh.BL.y} Z`;
                    return (
                        <path
                            key={i}
                            ref={el => (shapeRefs.current[i] = el)}
                            d={d}
                            fill="none"
                            stroke={`rgba(255,255,255,${shapeOpacity(i)})`}
                            strokeWidth="2"
                            vectorEffect="non-scaling-stroke"
                            style={{
                                opacity: phase === 'menuReady' ? 1 : 0,
                                transform: phase === 'menuReady' ? 'translateY(0)' : 'translateY(900px)',
                                transformBox: 'fill-box',
                                transformOrigin: 'center center',
                                transition: 'opacity 1s ease-out, transform 1s ease-out',
                            }}
                        />
                    );
                })}

                {MENU_ITEMS.map((item, i) => {
                    const sh = shapes[i + 1];
                    const labelX = sh.TL.x + 2;
                    const labelY = sh.TL.y + LABEL_Y_OFFSET;
                    return (
                        <text
                            key={item.route}
                            ref={el => (labelRefs.current[i] = el)}
                            x={labelX}
                            y={labelY}
                            fontFamily="'Poppins', sans-serif"
                            fontWeight="300"
                            fontSize="34"
                            textAnchor="start"
                            fill="rgba(255,255,255,0.65)"
                            style={{
                                textTransform: 'uppercase',
                                letterSpacing: '0.04em',
                                cursor: 'pointer',
                                pointerEvents: 'all',
                                opacity: phase === 'menuReady' ? 1 : 0,
                                transform: phase === 'menuReady' ? 'translateY(0)' : 'translateY(900px)',
                                transformBox: 'fill-box',
                                transformOrigin: 'center bottom',
                                filter: 'drop-shadow(0 1px 0 rgba(0,0,0,0.15))',
                                transition: 'fill 0.2s ease',
                                userSelect: 'none',
                            }}
                            onClick={() => navTo(item.route)}
                            onMouseEnter={e => e.currentTarget.setAttribute('fill', '#fff')}
                            onMouseLeave={e => e.currentTarget.setAttribute('fill', 'rgba(255,255,255,0.65)')}
                        >
                            {item.label.toUpperCase()}
                        </text>
                    );
                })}
            </svg>
            </div>
        </>
    );
}
